import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  Hash, 
  Plus, 
  Pencil, 
  Trash2, 
  Clock, 
  X, 
  Check,
  AlertTriangle,
  Bike,
  Euro,
  Search,
  Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import SeasonPricing from '../components/SeasonPricing';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('veloflux_token')}` };
}

export default function Models() {
  const { isAdmin } = useAuth();
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ nombre_modelo: '', numero_modelo: '', revision_period_days: '', categoria: '' });
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newModel, setNewModel] = useState({ nombre_modelo: '', numero_modelo: '', revision_period_days: '', categoria: '' });

  const [categorias, setCategorias] = useState([]);
  const [showCatDialog, setShowCatDialog] = useState(false);
  const [newCat, setNewCat] = useState('');
  const LOKKI_CATEGORIES = categorias.map(c => c.nombre);

  const fetchCategorias = async () => {
    try {
      const res = await axios.get(`${API}/catalog/categorias`, { headers: getHeaders() });
      setCategorias(res.data || []);
    } catch {}
  };

  const handleAddCategoria = async (e) => {
    e.preventDefault();
    if (!newCat.trim()) return;
    try {
      await axios.post(`${API}/catalog/categorias`, { nombre: newCat.trim() }, { headers: getHeaders() });
      toast.success(`Categoría "${newCat.trim()}" añadida`);
      setNewCat('');
      fetchCategorias();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al añadir categoría');
    }
  };

  const handleDeleteCategoria = async (cat) => {
    if (!window.confirm(`¿Eliminar la categoría "${cat.nombre}"? Los modelos y bicicletas que ya la tienen la conservarán como texto.`)) return;
    try {
      const res = await axios.delete(`${API}/catalog/categorias/${cat.id}`, { headers: getHeaders() });
      const used = res.data?.models_using || 0;
      toast.success(used > 0
        ? `Categoría eliminada (${used} modelo(s) la conservan asignada)`
        : 'Categoría eliminada');
      fetchCategorias();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar categoría');
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [bikeCounts, setBikeCounts] = useState({});
  
  // Pricing tab
  const [activeTab, setActiveTab] = useState('models');
  const [pricing, setPricing] = useState([]);
  const [pricingFilter, setPricingFilter] = useState('all');
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  const [pricingForm, setPricingForm] = useState({ model_name: '', year: new Date().getFullYear(), price: '' });

  const fetchModels = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/catalog/models`, { headers: getHeaders() });
      setModels(res.data);
      
      // Fetch bike counts for each model
      const counts = {};
      for (const model of res.data) {
        try {
          const countRes = await axios.get(`${API}/bicycles?model=${encodeURIComponent(model.nombre_modelo)}&count_only=true`, { headers: getHeaders() });
          counts[model.id] = countRes.data.count || 0;
        } catch {
          counts[model.id] = 0;
        }
      }
      setBikeCounts(counts);
    } catch (err) {
      toast.error('Error al cargar modelos');
    }
    setLoading(false);
  };

  const fetchNextNumero = async () => {
    try {
      const res = await axios.get(`${API}/catalog/models/next-numero`, { headers: getHeaders() });
      setNewModel(prev => ({ ...prev, numero_modelo: String(res.data.next_numero) }));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchModels();
    fetchPricing();
    fetchCategorias();
  }, []);

  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${API}/model-pricing`, { headers: getHeaders() });
      setPricing(res.data);
    } catch {}
  };

  const openPricingDialog = (item = null) => {
    if (item) {
      setEditingPricing(item);
      setPricingForm({ model_name: item.model_name, year: item.year, price: item.price });
    } else {
      setEditingPricing(null);
      setPricingForm({ model_name: models[0]?.nombre_modelo || '', year: new Date().getFullYear(), price: '' });
    }
    setPricingDialogOpen(true);
  };

  const savePricing = async () => {
    if (!pricingForm.model_name || !pricingForm.price) return toast.error('Modelo y precio obligatorios');
    try {
      if (editingPricing) {
        await axios.put(`${API}/model-pricing/${editingPricing.id}`, { price: parseFloat(pricingForm.price) }, { headers: getHeaders() });
        toast.success('Precio actualizado');
      } else {
        await axios.post(`${API}/model-pricing`, {
          model_name: pricingForm.model_name,
          year: parseInt(pricingForm.year),
          price: parseFloat(pricingForm.price)
        }, { headers: getHeaders() });
        toast.success('Precio añadido');
      }
      setPricingDialogOpen(false);
      fetchPricing();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const deletePricing = async (id) => {
    try {
      await axios.delete(`${API}/model-pricing/${id}`, { headers: getHeaders() });
      toast.success('Precio eliminado');
      fetchPricing();
    } catch { toast.error('Error al eliminar'); }
  };

  // Group pricing by model
  const pricingByModel = pricing
    .filter(p => pricingFilter === 'all' || p.model_name === pricingFilter)
    .reduce((acc, p) => {
      if (!acc[p.model_name]) acc[p.model_name] = [];
      acc[p.model_name].push(p);
      return acc;
    }, {});

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newModel.nombre_modelo.trim() || !newModel.numero_modelo) return;
    
    try {
      await axios.post(`${API}/catalog/models`, {
        numero_modelo: parseInt(newModel.numero_modelo),
        nombre_modelo: newModel.nombre_modelo.trim(),
        categoria: newModel.categoria.trim(),
        revision_period_days: newModel.revision_period_days ? parseInt(newModel.revision_period_days) : null
      }, { headers: getHeaders() });
      toast.success('Modelo creado correctamente');
      setShowAddDialog(false);
      setNewModel({ nombre_modelo: '', numero_modelo: '', revision_period_days: '', categoria: '' });
      fetchModels();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear modelo');
    }
  };

  const handleUpdate = async (id) => {
    try {
      await axios.put(`${API}/catalog/models/${id}`, {
        nombre_modelo: editForm.nombre_modelo.trim(),
        numero_modelo: parseInt(editForm.numero_modelo),
        categoria: editForm.categoria.trim(),
        revision_period_days: editForm.revision_period_days ? parseInt(editForm.revision_period_days) : null
      }, { headers: getHeaders() });
      toast.success('Modelo actualizado');
      setEditingId(null);
      fetchModels();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    }
  };

  const quickSetCategoria = async (model, categoria) => {
    try {
      await axios.put(`${API}/catalog/models/${model.id}`, { categoria }, { headers: getHeaders() });
      toast.success(categoria
        ? `Categoría "${categoria}" aplicada a ${model.nombre_modelo} y sus bicicletas`
        : `Categoría quitada de ${model.nombre_modelo}`);
      fetchModels();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar categoría');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/catalog/models/${id}`, { headers: getHeaders() });
      toast.success('Modelo eliminado');
      setDeleteConfirm(null);
      fetchModels();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar. Puede que haya bicicletas usando este modelo.');
    }
  };

  const startEdit = (model) => {
    setEditingId(model.id);
    setEditForm({
      nombre_modelo: model.nombre_modelo,
      numero_modelo: String(model.numero_modelo),
      revision_period_days: model.revision_period_days ? String(model.revision_period_days) : '',
      categoria: model.categoria || ''
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ nombre_modelo: '', numero_modelo: '', revision_period_days: '', categoria: '' });
  };

  return (
    <div className="space-y-6" data-testid="models-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">
            Modelos
          </h1>
          <p className="text-slate-500 mt-1">
            Gestiona los modelos de bicicletas, revisiones y precios por año
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && activeTab === 'models' && (
            <Button
              variant="outline"
              onClick={() => setShowCatDialog(true)}
              data-testid="manage-categorias-btn"
            >
              Categorías (Lokki)
            </Button>
          )}
          {isAdmin && activeTab === 'models' && (
            <Button 
              onClick={() => { setShowAddDialog(true); fetchNextNumero(); }}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="add-model-btn"
            >
              <Plus size={18} className="mr-2" />
              Nuevo Modelo
            </Button>
          )}
          {isAdmin && activeTab === 'pricing' && (
            <Button 
              onClick={() => openPricingDialog()}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="add-pricing-btn"
            >
              <Plus size={18} className="mr-2" />
              Añadir Precio
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button onClick={() => setActiveTab('models')}
          className={`px-4 py-2 font-medium text-sm transition-colors relative ${activeTab === 'models' ? 'text-orange-600' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-models">
          <span className="flex items-center gap-2"><Hash size={16} /> Modelos</span>
          {activeTab === 'models' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" />}
        </button>
        <button onClick={() => setActiveTab('pricing')}
          className={`px-4 py-2 font-medium text-sm transition-colors relative ${activeTab === 'pricing' ? 'text-orange-600' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-pricing">
          <span className="flex items-center gap-2"><Euro size={16} /> Precios por Año
            {pricing.length > 0 && <Badge className="bg-orange-100 text-orange-700 text-xs px-1.5 py-0">{pricing.length}</Badge>}
          </span>
          {activeTab === 'pricing' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" />}
        </button>
        <button onClick={() => setActiveTab('seasons')}
          className={`px-4 py-2 font-medium text-sm transition-colors relative ${activeTab === 'seasons' ? 'text-orange-600' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-seasons">
          <span className="flex items-center gap-2"><Euro size={16} /> Tarifas Temporada</span>
          {activeTab === 'seasons' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" />}
        </button>
      </div>

      {activeTab === 'seasons' && <SeasonPricing isAdmin={isAdmin} />}

      {activeTab === 'models' && (
      <>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Hash className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Modelos</p>
                <p className="text-2xl font-bold text-slate-900">{models.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Con Control de Revisión</p>
                <p className="text-2xl font-bold text-slate-900">
                  {models.filter(m => m.revision_period_days).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Bike className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Sin Límite de Revisión</p>
                <p className="text-2xl font-bold text-slate-900">
                  {models.filter(m => !m.revision_period_days).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Models Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-['Barlow_Condensed']">Lista de Modelos</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-400">Cargando...</div>
          ) : models.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Hash className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No hay modelos registrados</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Nº</TableHead>
                    <TableHead>Nombre del Modelo</TableHead>
                    <TableHead className="w-48">Categoría (Lokki)</TableHead>
                    <TableHead className="w-40">Período Revisión</TableHead>
                    <TableHead className="w-32 text-center">Bicicletas</TableHead>
                    {isAdmin && <TableHead className="w-32 text-right">Acciones</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {models.map((model) => (
                    <TableRow key={model.id} data-testid={`model-row-${model.id}`}>
                      {editingId === model.id ? (
                        <>
                          <TableCell>
                            <Input
                              type="number"
                              min="1"
                              value={editForm.numero_modelo}
                              onChange={(e) => setEditForm({ ...editForm, numero_modelo: e.target.value })}
                              className="w-16"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={editForm.nombre_modelo}
                              onChange={(e) => setEditForm({ ...editForm, nombre_modelo: e.target.value })}
                              placeholder="Nombre del modelo"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              list="lokki-cat-suggestions"
                              value={editForm.categoria}
                              onChange={(e) => setEditForm({ ...editForm, categoria: e.target.value })}
                              placeholder="Ej: E-Bike Road"
                              data-testid={`edit-model-categoria-${model.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="1"
                                value={editForm.revision_period_days}
                                onChange={(e) => setEditForm({ ...editForm, revision_period_days: e.target.value })}
                                placeholder="días"
                                className="w-20"
                              />
                              <span className="text-sm text-slate-500">días</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">{bikeCounts[model.id] || 0}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="ghost" onClick={() => handleUpdate(model.id)}>
                                <Check size={16} className="text-green-600" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={cancelEdit}>
                                <X size={16} className="text-slate-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>
                            <Badge variant="outline" className="font-mono bg-orange-50 text-orange-700">
                              {model.numero_modelo}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium">{model.nombre_modelo}</TableCell>
                          <TableCell>
                            {isAdmin ? (
                              <Select
                                value={model.categoria || '__none__'}
                                onValueChange={(v) => quickSetCategoria(model, v === '__none__' ? '' : v)}
                              >
                                <SelectTrigger
                                  className={`h-8 w-44 ${model.categoria ? 'border-green-300 bg-green-50 text-green-800' : 'text-slate-400'}`}
                                  data-testid={`categoria-select-${model.id}`}
                                >
                                  <SelectValue placeholder="Sin categoría" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none__">Sin categoría</SelectItem>
                                  {[...new Set([...LOKKI_CATEGORIES, ...(model.categoria ? [model.categoria] : [])])].map(c => (
                                    <SelectItem key={c} value={c}>{c}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : model.categoria ? (
                              <Badge className="bg-green-100 text-green-700 border-green-200 w-fit">{model.categoria}</Badge>
                            ) : (
                              <span className="text-slate-400 text-sm">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {model.revision_period_days ? (
                              <Badge className="bg-blue-100 text-blue-700 border-blue-200 flex items-center gap-1 w-fit">
                                <Clock size={12} />
                                {model.revision_period_days} días
                              </Badge>
                            ) : (
                              <span className="text-slate-400 text-sm">Sin límite</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">{bikeCounts[model.id] || 0}</Badge>
                          </TableCell>
                          {isAdmin && (
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => startEdit(model)}
                                  data-testid={`edit-model-${model.id}`}
                                >
                                  <Pencil size={16} className="text-slate-500" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => setDeleteConfirm(model)}
                                  className="text-red-500 hover:text-red-700"
                                  data-testid={`delete-model-${model.id}`}
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Período de Revisión</p>
              <p>
                El período de revisión define cada cuántos días una bicicleta de este modelo 
                debe pasar revisión. Si se supera este período sin revisión, la bicicleta 
                aparecerá marcada como "pendiente de revisión" en el listado y en las notificaciones.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      </>
      )}

      {/* Pricing Tab */}
      {activeTab === 'pricing' && (
        <>
          {/* Filter */}
          <div className="flex gap-4 items-end">
            <div className="w-64">
              <Label className="text-xs text-slate-500">Filtrar por modelo</Label>
              <Select value={pricingFilter} onValueChange={setPricingFilter}>
                <SelectTrigger data-testid="pricing-model-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los modelos</SelectItem>
                  {[...new Set(pricing.map(p => p.model_name))].sort().map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {Object.keys(pricingByModel).length === 0 ? (
            <Card><CardContent className="p-8 text-center text-slate-400">No hay precios registrados. Añade el primer precio con el botón superior.</CardContent></Card>
          ) : (
            Object.entries(pricingByModel).sort(([a], [b]) => a.localeCompare(b)).map(([modelName, prices]) => (
              <Card key={modelName} data-testid={`pricing-group-${modelName}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-['Barlow_Condensed'] flex items-center gap-2">
                    <Bike size={18} className="text-orange-500" />
                    {modelName}
                    <Badge variant="outline" className="text-xs ml-2">{prices.length} año{prices.length !== 1 ? 's' : ''}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Año</TableHead>
                        <TableHead>Precio</TableHead>
                        {isAdmin && <TableHead className="w-24">Acciones</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {prices.sort((a, b) => b.year - a.year).map(p => (
                        <TableRow key={p.id} data-testid={`pricing-row-${p.id}`}>
                          <TableCell className="font-semibold">{p.year}</TableCell>
                          <TableCell className="font-semibold text-green-700">
                            {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(p.price)}
                          </TableCell>
                          {isAdmin && (
                            <TableCell>
                              <div className="flex gap-1">
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                                  onClick={() => openPricingDialog(p)} data-testid={`edit-pricing-${p.id}`}>
                                  <Pencil size={14} />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500"
                                  onClick={() => deletePricing(p.id)} data-testid={`delete-pricing-${p.id}`}>
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ))
          )}
        </>
      )}

      {/* Pricing Dialog */}
      <Dialog open={pricingDialogOpen} onOpenChange={setPricingDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPricing ? 'Editar Precio' : 'Añadir Precio'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Modelo</Label>
              <Select value={pricingForm.model_name} onValueChange={(v) => setPricingForm({ ...pricingForm, model_name: v })}
                disabled={!!editingPricing}>
                <SelectTrigger data-testid="pricing-model-select"><SelectValue placeholder="Seleccionar modelo..." /></SelectTrigger>
                <SelectContent>
                  {models.map(m => (
                    <SelectItem key={m.id} value={m.nombre_modelo}>{m.numero_modelo} - {m.nombre_modelo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Año</Label>
              <Input type="number" min="2000" max="2100" value={pricingForm.year}
                onChange={(e) => setPricingForm({ ...pricingForm, year: e.target.value })}
                disabled={!!editingPricing} data-testid="pricing-year-input" />
            </div>
            <div className="space-y-2">
              <Label>Precio (EUR)</Label>
              <Input type="number" step="0.01" min="0" value={pricingForm.price}
                onChange={(e) => setPricingForm({ ...pricingForm, price: e.target.value })}
                placeholder="0.00" data-testid="pricing-price-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={savePricing} className="bg-orange-500 hover:bg-orange-600" data-testid="save-pricing-btn">
              {editingPricing ? 'Actualizar' : 'Añadir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Model Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent data-testid="add-model-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Hash className="w-5 h-5 text-orange-500" />
              Nuevo Modelo
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Nº Modelo</Label>
                <Input
                  type="number"
                  min="1"
                  value={newModel.numero_modelo}
                  onChange={(e) => setNewModel({ ...newModel, numero_modelo: e.target.value })}
                  required
                  data-testid="new-model-numero"
                />
              </div>
              <div className="col-span-3">
                <Label>Nombre del Modelo</Label>
                <Input
                  value={newModel.nombre_modelo}
                  onChange={(e) => setNewModel({ ...newModel, nombre_modelo: e.target.value })}
                  placeholder="Ej: Orbea Orca Aero M10"
                  required
                  data-testid="new-model-name"
                />
              </div>
            </div>
            <div>
              <Label>Categoría (Lokki)</Label>
              <Input
                list="lokki-cat-suggestions"
                value={newModel.categoria}
                onChange={(e) => setNewModel({ ...newModel, categoria: e.target.value })}
                placeholder="Ej: E-Bike Step-Through (opcional)"
                data-testid="new-model-categoria"
              />
              <p className="text-xs text-slate-500 mt-1">
                Las bicicletas de este modelo heredan esta categoría automáticamente (salvo que tengan una propia).
              </p>
            </div>
            <datalist id="lokki-cat-suggestions">
              {LOKKI_CATEGORIES.map(c => <option key={c} value={c} />)}
            </datalist>
            <div>
              <Label className="flex items-center gap-2">
                <Clock size={14} />
                Período de Revisión (días)
              </Label>
              <Input
                type="number"
                min="1"
                value={newModel.revision_period_days}
                onChange={(e) => setNewModel({ ...newModel, revision_period_days: e.target.value })}
                placeholder="Dejar vacío para sin límite"
                data-testid="new-model-revision"
              />
              <p className="text-xs text-slate-500 mt-1">
                Opcional. Si se define, las bicicletas de este modelo que superen este período sin revisión serán marcadas.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-orange-500 hover:bg-orange-600">
                Crear Modelo
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Lokki categories management */}
      <Dialog open={showCatDialog} onOpenChange={setShowCatDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Categorías (Lokki)</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddCategoria} className="flex gap-2">
            <Input
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              placeholder="Nueva categoría, ej: Gravel"
              data-testid="new-categoria-input"
            />
            <Button type="submit" className="bg-orange-500 hover:bg-orange-600 shrink-0" data-testid="add-categoria-btn">
              <Plus size={16} className="mr-1" />
              Añadir
            </Button>
          </form>
          <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="categorias-list">
            {categorias.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">No hay categorías</p>
            ) : categorias.map(cat => (
              <div key={cat.id} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-slate-50" data-testid={`categoria-row-${cat.id}`}>
                <span className="text-sm font-medium text-slate-700">{cat.nombre}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDeleteCategoria(cat)}
                  data-testid={`delete-categoria-${cat.id}`}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400">
            Al eliminar una categoría, los modelos y bicicletas que ya la tenían la conservan como texto.
          </p>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Eliminar Modelo</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            ¿Estás seguro de que quieres eliminar el modelo <strong>{deleteConfirm?.nombre_modelo}</strong>?
          </p>
          {(bikeCounts[deleteConfirm?.id] || 0) > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
              <strong>Advertencia:</strong> Este modelo tiene {bikeCounts[deleteConfirm?.id]} bicicletas asociadas.
              No podrás eliminarlo mientras existan bicicletas con este modelo.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm?.id)}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
